import { useIntl } from 'react-intl';
import { Checkbox, Col, Input, Row, Select } from 'antd';

import { ExtraFiltersProps } from '@/types/ExtraFiltersProps';

import {
  CompaniesInfiniteSelectField,
  DocumentTypesInfiniteSelectField,
  InputDate,
  InputDecimal,
} from '@/components/_common';
import moment from 'moment';
import {
  InstallmentSituation,
  ListInstallmentsQuery,
} from '@/core/domain/financial/AccountPayable/AccountPayable.types';
import OrderExtraFilter from '@/components/_common/ExtraFilters/OrderExtraFilter';

function InstallmentsExtraFilters({
  queryParams,
  setQueryParams,
}: ExtraFiltersProps<ListInstallmentsQuery>) {
  const intl = useIntl();

  return (
    <Row gutter={[16, 16]}>
      <Col xs={24} sm={24} lg={4} md={12}>
        <Input
          value={queryParams?.code}
          onChange={({ target: { value } }) =>
            setQueryParams((prev) => ({ ...prev, code: value }))
          }
          placeholder={intl.formatMessage({
            id: 'releaseBillToPay.list.header.code',
          })}
        />
      </Col>

      <Col xs={24} sm={24} lg={8} md={12}>
        <CompaniesInfiniteSelectField
          value={queryParams?.companyId}
          placeholder={intl.formatMessage({
            id: 'releaseBillToPay.list.header.companies',
          })}
          allowClear
          style={{ width: '100%' }}
          onChange={(values) =>
            setQueryParams((prev) => ({ ...prev, companyId: values }))
          }
        />
      </Col>

      <Col xs={24} sm={24} lg={4} md={12}>
        <Input
          value={queryParams?.documentNumber}
          onChange={({ target: { value } }) =>
            setQueryParams((prev) => ({ ...prev, documentNumber: value }))
          }
          placeholder={intl.formatMessage({
            id: 'releaseBillToPay.list.header.documentNumber',
          })}
        />
      </Col>

      <Col xs={24} sm={24} lg={4} md={12}>
        <Input
          value={queryParams?.creditor}
          onChange={({ target: { value } }) =>
            setQueryParams((prev) => ({ ...prev, creditor: value }))
          }
          placeholder={intl.formatMessage({
            id: 'releaseBillToPay.list.header.creditor',
          })}
        />
      </Col>

      <Col xs={24} sm={24} lg={8} md={12}>
        <Input
          value={queryParams?.description}
          onChange={({ target: { value } }) =>
            setQueryParams((prev) => ({ ...prev, description: value }))
          }
          placeholder={intl.formatMessage({
            id: 'releaseBankEntry.list.header.description',
          })}
        />
      </Col>

      <Col xs={24} sm={24} lg={8} md={12}>
        <DocumentTypesInfiniteSelectField
          value={queryParams?.documentTypeId}
          placeholder={intl.formatMessage({
            id: 'releaseBillToPay.list.header.documentType',
          })}
          allowClear
          style={{ width: '100%' }}
          onChange={(values) =>
            setQueryParams((prev) => ({ ...prev, documentTypeId: values }))
          }
        />
      </Col>

      <Col xs={24} sm={24} lg={4} md={12}>
        <InputDate
          placeholder="Data de emissão"
          value={
            queryParams?.emissionDate
              ? moment(queryParams?.emissionDate)
              : undefined
          }
          onChange={(value) =>
            setQueryParams((prev) => ({
              ...prev,
              emissionDate: value?.toISOString(),
            }))
          }
        />
      </Col>

      <Col xs={24} sm={24} lg={4} md={12}>
        <InputDate
          placeholder={intl.formatMessage({
            id: 'releaseBillToPay.list.header.dueDate',
          })}
          value={
            queryParams?.dueDate ? moment(queryParams?.dueDate) : undefined
          }
          onChange={(value) =>
            setQueryParams((prev) => ({
              ...prev,
              dueDate: value?.toISOString(),
            }))
          }
        />
      </Col>

      <Col xs={24} sm={24} lg={4} md={12}>
        <InputDate
          placeholder="Data de pagamento"
          value={
            queryParams?.payDate ? moment(queryParams?.payDate) : undefined
          }
          onChange={(value) =>
            setQueryParams((prev) => ({
              ...prev,
              payDate: value?.toISOString(),
            }))
          }
        />
      </Col>

      <Col xs={24} sm={24} lg={4} md={12}>
        <Select
          allowClear
          style={{ width: '100%' }}
          value={queryParams?.situation}
          onChange={(value) => {
            setQueryParams((prev) => ({ ...prev, situation: value }));
          }}
          placeholder={intl.formatMessage({
            id: 'releaseBillToPay.list.header.situation',
          })}
          options={[
            {
              value: InstallmentSituation.open,
              label: intl.formatMessage({
                id: 'domain.enums.situation_installment_payable.open',
              }),
            },
            {
              value: InstallmentSituation.settled,
              label: intl.formatMessage({
                id: 'domain.enums.situation_installment_payable.settled',
              }),
            },
          ]}
        />
      </Col>

      <Col xs={24} sm={24} lg={3} md={12}>
        <InputDecimal
          value={queryParams?.installmentValue}
          onChange={(value) =>
            setQueryParams((prev) => ({
              ...prev,
              installmentValue: value,
            }))
          }
          placeholder={intl.formatMessage({
            id: 'releaseBillToPay.list.header.value',
          })}
        />
      </Col>

      <Col xs={24} sm={24} lg={4} md={12}>
        <OrderExtraFilter
          value={queryParams?.order}
          onChange={(value) =>
            setQueryParams((prev) => ({
              ...prev,
              order: value,
            }))
          }
        />
      </Col>

      <Col xs={24} sm={24} lg={6} md={12}>
        <Checkbox
          onChange={(e) => {
            if (e.target.checked) {
              setQueryParams((prev) => ({
                ...prev,
                rows: 500,
              }));
            } else {
              setQueryParams((prev) => ({
                ...prev,
                rows: 10,
              }));
            }
          }}
          style={{ fontWeight: 'bold' }}
        >
          Listar todos
        </Checkbox>
      </Col>

      <Col span={24}>
        <Input.TextArea
          placeholder="Observação"
          onChange={({ target: { value } }) =>
            setQueryParams((prev) => ({ ...prev, observation: value }))
          }
          autoSize={{ minRows: 3, maxRows: 3 }}
        />
      </Col>
    </Row>
  );
}

export default InstallmentsExtraFilters;
